import React, {useReducer, useContext, useEffect, useCallback, useRef, useMemo} from "react";
import {BrandContext} from "@context/BrandContext";
import {Tabs, Row, Button, Progress} from "antd";
import CircularLoader from "@common/CircularLoader";
import axios from "@common/axios";
import Question, {QuestionAnswerResultBlock} from "./Question";
import Alert from "@common/Alert";
import PracticeQuestionsResults from "./PracticeQuestionsResults";
import TrailFeedback from "./TrailFeedback";
import {CheckCircleOutlined, CloseCircleOutlined} from "@ant-design/icons";
import {RESULT_VIEWS} from "./questionTypes/DragAndDropQuestion";
import LevelMarker from "./LevelMarker";

const ACTIONS = {
    LOAD_QUESTIONS_LIST: "LOAD_QUESTIONS_LIST",
    LOAD_QUESTIONS_LIST_SUCCESS: "LOAD_QUESTIONS_LIST_SUCCESS",
    CHANGE_LEVEL: "CHANGE_LEVEL",
    LOAD_QUESTION: "LOAD_QUESTION",
    LOAD_QUESTION_SUCCESS: "LOAD_QUESTION_SUCCESS",
    FIX_QUESTION_ID: "FIX_QUESTION_ID",
    UPDATE_RESULTS: "UPDATE_RESULTS",
    UPDATE_RESULTS_FILTER: "UPDATE_RESULTS_FILTER",
    UPDATE_SUBMITTABLE: "UPDATE_SUBMITTABLE",
    SUBMIT_QUESTION: "SUBMIT_QUESTION",
    SUBMIT_QUESTION_SUCCESS: "SUBMIT_QUESTION_SUCCESS",
    AJAX_ERROR: "AJAX_ERROR",
    UPDATE_DND_RESULTS_VIEW: "UPDATE_DND_RESULTS_VIEW",
}

const questionsInitialState = {
    isLoading: true,
    error: null,
    levels: {},
    hasQuestions: null,
    questionLevel: null,
    fixedQuestion: false,
    currentQuestionId: null,
    currentQuestion: null,
    questionSubmittable: false,
    questionAnswered: false,
    questionResult: null,
    results: {},
    previousAnswerIncorrect: false,
    resultsFilter: null,
    dndResultView: RESULT_VIEWS.MY_ANSWERS,
}

const resetQuestionState = {
    error: questionsInitialState.error,
    currentQuestion: questionsInitialState.currentQuestion,
    questionSubmittable: questionsInitialState.questionSubmittable,
    questionAnswered: questionsInitialState.questionAnswered,
    questionResult: questionsInitialState.questionResult,
    dndResultView: questionsInitialState.dndResultView,
}

function questionsReducer(state, action) {
    if (action.type === ACTIONS.LOAD_QUESTIONS_LIST) {
        return {...state, isLoading: action.payload, error: null};
    } else if (action.type === ACTIONS.LOAD_QUESTIONS_LIST_SUCCESS) {
        return {
            ...state,
            isLoading: false,
            levels: action.payload.levels,
            hasQuestions: action.payload.hasQuestions,
        };
    } else if (action.type === ACTIONS.CHANGE_LEVEL) {
        if (state.questionLevel === action.payload) return state;
        return {
            ...state,
            questionLevel: action.payload,
            fixedQuestion: questionsInitialState.fixedQuestion,
            currentQuestionId: questionsInitialState.currentQuestionId,
            ...resetQuestionState,
        };
    } else if (action.type === ACTIONS.LOAD_QUESTION) {
        return {...state, ...resetQuestionState};
    } else if (action.type === ACTIONS.LOAD_QUESTION_SUCCESS) {
        return {
            ...state,
            currentQuestionId: action.payload.questionId,
            currentQuestion: action.payload,
        }
    } else if (action.type === ACTIONS.FIX_QUESTION_ID) {
        const extraReset = String(state.currentQuestion?.questionId) !== String(action.payload) ? resetQuestionState : {};
        return {
            ...state,
            fixedQuestion: true,
            currentQuestionId: action.payload,
            ...extraReset
        }
    } else if (action.type === ACTIONS.UPDATE_RESULTS) {
        return {...state, results: action.payload};
    } else if (action.type === ACTIONS.UPDATE_RESULTS_FILTER) {
        return {...state, resultsFilter: action.payload};
    } else if (action.type === ACTIONS.UPDATE_SUBMITTABLE) {
        return {...state, questionSubmittable: action.payload};
    } else if (action.type === ACTIONS.SUBMIT_QUESTION) {
        return {...state, questionAnswered: true};
    } else if (action.type === ACTIONS.SUBMIT_QUESTION_SUCCESS) {
        return {
            ...state,
            questionResult: action.payload.questionResult,
            previousAnswerIncorrect: action.payload.questionResult.result === false,
            levels: action.payload.levels
        };
    } else if (action.type === ACTIONS.AJAX_ERROR) {
        return {...state, isLoading: false, error: action.payload};
    } else if (action.type === ACTIONS.UPDATE_DND_RESULTS_VIEW) {
        return {...state, dndResultView: action.payload};
    } else {
        return state;
    }
}

const PracticeQuestionsCore = props => {
    const {conceptId, questionLevel, displayHeader = true, previewMode = false, onLevelChange} = props;
    const brandConfig = useContext(BrandContext);
    const [state, dispatch] = useReducer(questionsReducer, questionsInitialState);
    const answerRef = useRef("");
    const abortControllerRef = useRef(null);

    useEffect(() => {
        const controller = new AbortController();
        const loadQuestionsList = async () => {
            dispatch({type: ACTIONS.LOAD_QUESTIONS_LIST, payload: true});
            try {
                const response = await axios.get('/web/ui/question/list', {
                    params: {conceptId: conceptId},
                    signal: controller.signal,
                });

                const levels = {};
                const countsByLevel = response.data.questionCountsByLevel || {};

                response.data.levelsData.forEach(level => {
                    const availableCount = countsByLevel[level.value] || 0;
                    levels[level.value] = {
                        ...level,
                        hasAvailableQuestions: availableCount > 0
                    };
                });
                const hasQuestions = response.data.lessonHasQuestions;

                dispatch({
                    type: ACTIONS.LOAD_QUESTIONS_LIST_SUCCESS,
                    payload: {levels: levels, hasQuestions}
                });
            } catch(error) {
                if (error.name === 'AbortError') return;
                dispatch({type: ACTIONS.AJAX_ERROR, payload: 'Failed to load questions'});
            }
        }

        loadQuestionsList();

        return () => {
            controller.abort();
            if (abortControllerRef.current) abortControllerRef.current.abort();
        };
    }, [conceptId]);

    useEffect(() => {
        if (!state.levels || Object.keys(state.levels).length === 0) return;

        const levelObj = state.levels[questionLevel];
        const isValid = levelObj != null && levelObj.hasAvailableQuestions;
        const nextLevel = isValid ? questionLevel : null;

        if (nextLevel !== state.questionLevel) {
            if (questionLevel && !isValid) {
                updateQuestionLevel(null);
            }
            dispatch({type: ACTIONS.CHANGE_LEVEL, payload: nextLevel});
        }
    }, [questionLevel, state.levels, state.questionLevel]);

    useEffect(() => {
        if (questionLevel !== state.questionLevel) return;

        if (!state.questionLevel) return;

        const controller = new AbortController();
        const loadResults = async () => {
            try {
                const response = await axios.get("/web/ui/question/results", {
                    params: {conceptId: conceptId, level: state.questionLevel, ...state.resultsFilter ?? {}},
                    signal: controller.signal,
                });
                dispatch({type: ACTIONS.UPDATE_RESULTS, payload: response.data});
            } catch (error) {
                if (error.name === 'AbortError') return;
                dispatch({type: ACTIONS.AJAX_ERROR, payload: 'Failed to load question results'});
            }
        }

        loadResults();

        return () => controller.abort();
    }, [conceptId, state.questionLevel, questionLevel, state.resultsFilter, state.questionResult]);

    useEffect(() => {
        if (!state.questionLevel) return;

        if (state.currentQuestion && String(state.currentQuestion.questionId) === String(state.currentQuestionId)) {
            return;
        }

        const controller = new AbortController();
        const loadQuestion = async () => {
            dispatch({type: ACTIONS.LOAD_QUESTION});

            try {
                const qId = state.fixedQuestion ? state.currentQuestionId : null;
                const url = `/web/ui/question/load${qId ? `/${qId}` : ''}`;
                const response = await axios.get(url, {
                    params: {conceptId, level: state.questionLevel},
                    signal: controller.signal
                });

                dispatch({type: ACTIONS.LOAD_QUESTION_SUCCESS, payload: response.data});
            } catch (error) {
                if (error.name === 'AbortError') return;
                dispatch({type: ACTIONS.AJAX_ERROR, payload: 'Failed to load question. Please try again.'});
            }
        };

        loadQuestion();

        return () => controller.abort();
    }, [conceptId, state.questionLevel, state.currentQuestionId, state.currentQuestion, state.fixedQuestion]);

    useEffect(() => {
        if (!state.questionResult) return;
    }, [state.questionResult?.levelNewlyAchieved])

    const updateQuestionLevel = level => {
        onLevelChange?.(level);
    }

    const updateResultsFilter = filter => {
        dispatch({type: ACTIONS.UPDATE_RESULTS_FILTER, payload: filter});
    }

    const handleUpdateQuestionData = useCallback((questionAnswers, isSubmittable) => {
        answerRef.current = questionAnswers;
        dispatch({type: ACTIONS.UPDATE_SUBMITTABLE, payload: isSubmittable});
    }, []);

    const handleSubmit = event => {
        event.preventDefault();

        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();

        dispatch({type: ACTIONS.SUBMIT_QUESTION});

        const submitQuestion = async () => {
            try {
                const answerBody = answerRef.current;
                let params = {
                    conceptId: conceptId,
                    level: state.questionLevel,
                    questionId: state.currentQuestionId,
                }
                if (previewMode) {
                    params.preview = true;
                } else {
                    params.previousAnswerIncorrect = state.previousAnswerIncorrect;
                }
                const {data} = await axios.post('/web/ui/question/answer',
                    answerBody,
                    {
                        params: params,
                        signal: abortControllerRef.current.signal
                    }
                );
                const questionResult = {
                    result: data.result,
                    correctAnswerFeedback: data.correctAnswerFeedback,
                    practiceQuestionFeedback: data.practiceQuestionFeedback,
                    noLevelFeedback: data.noLevelFeedback,
                    levelNewlyAchieved: data.levelNewlyAchieved,
                }
                const newLevels = {
                    ...state.levels,
                    [state.questionLevel]: {
                        ...state.levels[state.questionLevel],
                        attemptCount: data.attemptCount,
                        correctCount: data.correctCount,
                        levelAchieved:
                            data.levelNewlyAchieved
                                ? true
                                : state.levels[state.questionLevel].levelAchieved
                    }
                };

                dispatch({type: ACTIONS.SUBMIT_QUESTION_SUCCESS, payload: {questionResult: questionResult, levels: newLevels}});
            } catch (error) {
                if (error.name === 'AbortError') return;
                dispatch({type: ACTIONS.AJAX_ERROR, payload: 'Error submitting question'});
            }
        }

        submitQuestion();
    }

    const handleContinue = () => {
        answerRef.current = "";
        dispatch({type: ACTIONS.LOAD_QUESTION});
    }

    const tabItems = useMemo(() => {
        return Object.values(state.levels).map(level => {
            const statusText = !level.hasAvailableQuestions ? "- Unavailable" : level.levelAchieved
                ? "- Achieved" : level.attemptCount > 0 ? "- Attempted" : "- Not started";

            return {
                key: level.value.toString(),
                disabled: !level.hasAvailableQuestions,
                label: (
                    <span className="flexRow flexAlignCentre flexSpaceBetween ch-question-level-tab-label ch-width-100">
                        <span className="ch-text-b">{level.description}</span>
                        <LevelMarker attempted={level.attemptCount > 0} achieved={level.levelAchieved} />
                        <span className="ch-sr-only">
                            {statusText}
                        </span>
                    </span>
                )
            };
        });
    }, [state.levels]);

    return (
        <>
            {displayHeader && (
                <h2 className="margin-b--l">{brandConfig.questionsName}</h2>
            )}
            {previewMode !== true && Object.keys(state.levels).length > 0 && (
                <Tabs
                    className="ch-tabs-lvl2"
                    items={tabItems}
                    activeKey={state.questionLevel ? state.questionLevel.toString() : "___UNSET___"}
                    onChange={updateQuestionLevel}
                />
            )}

            {state.isLoading && !state.error && (
                <Row>
                    <CircularLoader displayInElement={true}/>
                </Row>
            )}

            {state.error && (
                <Alert
                    type="error"
                    title={state.error}
                    className="margin-b--l"
                />
            )}

            {!state.isLoading && !state.error && (
                <>
                    {!state.hasQuestions ? (
                        <Alert
                            type="warning"
                            title={`There are no ${brandConfig.questionsName} for this ${brandConfig.conceptName}.`}
                        />
                    ) : (
                        <>
                            {state.questionLevel ? (
                                <>
                                    <PracticeQuestionsResults
                                        questionId={state.currentQuestionId}
                                        difficultyLevels={state.levels}
                                        previewMode={previewMode}
                                        results={state.results}
                                        loadQuestionById={questionId => dispatch({type: ACTIONS.FIX_QUESTION_ID, payload: questionId})}
                                        updateResultsFilter={updateResultsFilter}
                                    />
                                    <form
                                        className="ch-q-form"
                                        onSubmit={handleSubmit}
                                    >
                                        <Question
                                            question={state.currentQuestion}
                                            isLoading={state.currentQuestion === null}
                                            readOnly={state.questionAnswered}
                                            updateQuestionData={handleUpdateQuestionData}
                                            {...state.questionResult && {
                                                result: state.questionResult,
                                                dndResultView: state.dndResultView,
                                            }}
                                            displayAnswerOnResult={false}
                                        />
                                        {!state.questionAnswered && (
                                            <Row justify="end">
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    disabled={!state.questionSubmittable}
                                                >
                                                    Answer
                                                </Button>
                                            </Row>
                                        )}
                                    </form>
                                    {state.questionResult && (
                                        <TrailFeedback
                                            title={
                                                <>
                                                    {state.questionResult.result ? (
                                                        <CheckCircleOutlined className="ch-feedback-icon" aria-hidden="true"/>
                                                    ) : (
                                                        <CloseCircleOutlined className="ch-feedback-icon" aria-hidden="true"/>
                                                    )}
                                                    <h3 className="ch-feedback-title">{state.questionResult.result ? 'Correct' : 'Incorrect'}</h3>
                                                </>
                                            }
                                            buttons={
                                                <>
                                                    <Button
                                                        type="primary"
                                                        onClick={handleContinue}
                                                    >
                                                        Continue
                                                    </Button>
                                                    {previewMode !== true && (
                                                        <Button
                                                            onClick={() => updateQuestionLevel(null)}
                                                        >
                                                            {brandConfig.brandText.otherLevelsDescription}
                                                        </Button>
                                                    )}
                                                </>
                                            }
                                        >
                                            <QuestionAnswerResultBlock
                                                result={state.questionResult}
                                                questionAnswerType={state.currentQuestion.questionAnswerType}
                                                inDialog={true}
                                                dndValue={state.dndResultView}
                                                dndOnChange={view => dispatch({type: ACTIONS.UPDATE_DND_RESULTS_VIEW, payload: view})}
                                            />
                                            {previewMode !== true && Array.isArray(state.questionResult?.practiceQuestionFeedback) &&
                                                state.questionResult.practiceQuestionFeedback.map((feedback, index) => (
                                                    <p key={index} className="margin-t--m">{feedback}</p>
                                                ))
                                            }
                                        </TrailFeedback>
                                    )}
                                </>
                            ) : (
                                <>
                                    {Object.values(state.levels).map((level, index) => (
                                        <React.Fragment
                                            key={`levelList-${level.value}`}
                                        >
                                            {index > 0 && <hr/>}
                                            <h3 className="margin-t--l">{level.description}</h3>
                                            <div className="flex margin-t--m">
                                                <div className="flex1 margin-r--l">
                                                    <p>{brandConfig.brandText[`level${level.value}QuestionsDescription`]}</p>
                                                </div>
                                                <div className="margin-l--l">
                                                    <Progress
                                                        className="ch-pq-progress"
                                                        percent={(level.attemptCount / level.questionCount) * 100}
                                                        success={{percent: (level.correctCount / level.questionCount) * 100}}
                                                        type="circle"
                                                        size={40}
                                                        strokeWidth={20}
                                                        strokeLinecap="butt"
                                                        format={() => (
                                                            <span aria-label={`${level.correctCount} correct out of ${level.attemptCount} attempts`}></span>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </>
    )
};

export default PracticeQuestionsCore;